import { forwardRef, useState } from 'react';
import type { ButtonHTMLAttributes, MouseEvent as ReactMouseEvent, PointerEvent } from 'react';
import { BrainOrb } from './BrainOrb';
import './Mic.css';

interface MicProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  listening?: boolean;
  size?: 'touchline' | 'workbench';
  /** 'hold' = push-to-talk (default, field/touchline use). 'toggle' = click to
   *  arm, click again to end (long-form desktop dictation, e.g. onboarding). */
  mode?: 'hold' | 'toggle';
  onStart?: () => void;
  onStop?: () => void;
}

export const Mic = forwardRef<HTMLButtonElement, MicProps>(function Mic(
  {
    listening: controlledListening,
    size = 'touchline',
    mode = 'hold',
    onStart,
    onStop,
    onPointerDown,
    onPointerUp,
    onPointerCancel,
    onClick,
    className = '',
    ...rest
  },
  ref
) {
  const [internalListening, setInternalListening] = useState(false);
  const listening = controlledListening ?? internalListening;
  const orbSize = size === 'touchline' ? 'xl' : 'md';

  // Toggle mode — pointer events do nothing; the click handler flips state.
  const handleClick = (e: ReactMouseEvent<HTMLButtonElement>) => {
    if (mode !== 'toggle') {
      onClick?.(e);
      return;
    }
    if (listening) {
      if (controlledListening === undefined) setInternalListening(false);
      onStop?.();
    } else {
      if (controlledListening === undefined) setInternalListening(true);
      onStart?.();
    }
    onClick?.(e);
  };

  // Hold mode — pointerDown arms, pointerUp/cancel ends.
  const handleDown = (e: PointerEvent<HTMLButtonElement>) => {
    if (mode === 'toggle') {
      onPointerDown?.(e);
      return;
    }
    if (controlledListening === undefined) setInternalListening(true);
    onStart?.();
    onPointerDown?.(e);
  };
  const handleUp = (e: PointerEvent<HTMLButtonElement>) => {
    if (mode === 'toggle') {
      onPointerUp?.(e);
      return;
    }
    if (controlledListening === undefined) setInternalListening(false);
    onStop?.();
    onPointerUp?.(e);
  };
  const handleCancel = (e: PointerEvent<HTMLButtonElement>) => {
    if (mode === 'toggle') {
      onPointerCancel?.(e);
      return;
    }
    if (controlledListening === undefined) setInternalListening(false);
    onStop?.();
    onPointerCancel?.(e);
  };

  const ariaLabel = listening
    ? (mode === 'toggle' ? 'Listening — click to send now' : 'Listening — release to stop')
    : (mode === 'toggle' ? 'Click to talk' : 'Hold to talk');

  return (
    <button
      ref={ref}
      type="button"
      data-listening={listening}
      data-size={size}
      data-mode={mode}
      className={`mic ${className}`.trim()}
      aria-label={ariaLabel}
      aria-pressed={mode === 'toggle' ? listening : undefined}
      onClick={handleClick}
      onPointerDown={handleDown}
      onPointerUp={handleUp}
      onPointerCancel={handleCancel}
      {...rest}
    >
      <BrainOrb size={orbSize} state={listening ? 'listening' : 'idle'} />
    </button>
  );
});
